import { useContext, useEffect } from "react";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { createAppState } from "./AppState";
import { signal } from "@preact/signals-react";

export const cardMenuItems = signal([]);

// --- small matcher to pick "likeliest" card by name ---
function normalize(str) {
    return (str ?? "")
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, " ")
        .trim();
}

function scoreCardName(cardNameRaw, queryRaw) {
    const cardName = normalize(cardNameRaw);
    const query = normalize(queryRaw);

    if (!cardName || !query) return 0;
    if (cardName === query) return 1000; // exact match

    // strong signals
    if (cardName.startsWith(query)) return 900;
    if (cardName.includes(query)) return 800;

    // token overlap (Jaccard-ish)
    const a = new Set(cardName.split(" ").filter(Boolean));
    const b = new Set(query.split(" ").filter(Boolean));
    let overlap = 0;
    for (const t of b) if (a.has(t)) overlap++;
    const denom = Math.max(a.size, b.size) || 1;

    // smaller bonus if cardName includes most tokens
    return 100 + Math.round((overlap / denom) * 500);
}

export default function SelectCard() {
    const {
        cards,          // <-- signal/array of card objects
        projectName,    // <-- signal string (preferred search input)
        selectedCard,
        selectedProject,
        selectedDraw,
        isGlobalDisabled,
    } = useContext(createAppState);

    useEffect(() => {
        // Only auto-pick when we have a project + cards, and the control isn't disabled
        if (!selectedProject.value || isGlobalDisabled.value) return;

        const list = cards?.value ?? cards ?? [];
        if (!Array.isArray(list) || list.length === 0) return;

        // Use projectName if provided; fall back to selectedProject.value
        const query = projectName?.value || selectedProject.value;
        if (!query) return;

        // If user already selected a valid card, don't override
        // const current = selectedCard.value;
        // const idSet = new Set(list.map((c) => c?.id));
        // const hasSelection = current !== undefined && current !== null && current !== "";
        // const selectionStillValid = idSet.has(current);
        //
        // if (hasSelection && selectionStillValid) return;

        // Find best match by scoring card.name against the query
        let best = null;
        let bestScore = -1;

        for (const c of list) {
            const s = scoreCardName(c?.name, query);
            if (s > bestScore) {
                bestScore = s;
                best = c;
            }
        }

        // Only set if we found something meaningful
        if (best && bestScore > 0) {
            selectedCard.value = best.id;
        }
    }, [
        selectedProject.value,
        projectName?.value,
        cards?.value, // if cards is a signal
        selectedDraw.value,
        isGlobalDisabled.value,
    ]);

    return (
        <FormControl fullWidth>
            <InputLabel id="cards-menu" required>
                Card
            </InputLabel>
            <Select
                id="select-bhcip-cards"
                value={selectedCard.value}
                label="card *"
                onChange={(event) => {
                    selectedCard.value = event.target.value;
                }}
                disabled={!selectedProject.value || isGlobalDisabled.value}
                variant="filled"
            >
                {cardMenuItems.value}
            </Select>
        </FormControl>
    );
}
